import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Apartment,
  AutoStories,
  CalendarMonth,
  Dns,
  DocumentScanner,
  ExpandMore,
  FactCheck,
  GroupAdd,
  Groups,
  Insights,
  EmojiEmotions,
  Inventory2,
  LibraryBooks,
  Logout,
  ManageAccounts,
  MarkEmailRead,
  Menu as MenuIcon,
  MenuBook,
  MonetizationOn,
  PointOfSale,
  Quiz,
  ReceiptLong,
  Settings,
  Sms,
  SupportAgent,
  TodayOutlined,
  Tune,
  VideoLibrary,
} from "@mui/icons-material";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Suspense, useEffect, useState, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/shared/hooks/useAuth";
import { usePermission } from "@/shared/hooks/usePermission";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { apiClient } from "@/shared/api/apiClient";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { SchoolSwitcher } from "@/shared/components/SchoolSwitcher";
import { listFavorites } from "@/modules/favorites/favoritesApi";
import { favoriteLink } from "@/modules/favorites/favoriteLink";
import { ThemeControls } from "@/shared/components/ThemeControls";
import { analyticsApi } from "@/modules/analytics/analyticsApi";
import type { DashboardStats, FavoriteTargetType, RoleCode, SchoolDashboardStats } from "@/shared/api/types";

const DRAWER_WIDTH = 248;
const BAR_HEIGHT = 64;

const RouteFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
    <CircularProgress />
  </Box>
);

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  roles: RoleCode[];
  /** Etiketin altında küçük/muted açıklama satırı. */
  subLabel?: string;
  /** Menüde grup başlığı (ilk kez görüldüğünde bir overline etiket basılır). */
  group?: string;
  /** Okul menüsünde "Daha fazla" olmadan hep görünen sabit öğe (Ana Sayfa/Okul Yönetimi/Rehberlik). */
  pinned?: boolean;
}

// Okul kullanıcısının menüsünde "Daha fazla" altına giren (sabit-olmayan) rotalar — deep-link'te otomatik açılır.
// "/muhasebe" yalnız okul yöneticisinde ikincildir (muhasebecide sabit); useEffect'te açılması muhasebeci için zararsız.
const SECONDARY_PATHS = ["/muhasebe", "/mesaj-gonderim", "/ders-programi", "/ai-kullanim", "/defined-packages", "/denemeler"];

// Okula-özel modül rotaları (prefix): ilgili modül bayrağı kapalıysa menüde gizlenir ve doğrudan
// gidilse bile ana sayfaya yönlendirilir. Diğer tüm okul modülleri her zaman açıktır.
const SCHOOL_MODULE_PREFIXES = ["/muhasebe", "/mesaj-gonderim", "/ders-programi"];

const NAV: NavItem[] = [
  // NOT: "Ana Sayfa" (/) sol menüden kaldırıldı — logoya tıklayınca zaten ana sayfa açılır.
  // NOT: "Detaylı Analiz" (/analiz) sol menüden kaldırıldı — Ana Sayfa'daki "Detaylı Analiz" butonundan erişilir.
  { to: "/schools", label: "Okullar", icon: <Apartment />, roles: ["SYSTEM_ADMIN"] },
  { to: "/publishers", label: "Yayınevleri", icon: <MonetizationOn />, roles: ["SYSTEM_ADMIN"] },
  { to: "/content", label: "İçerik", icon: <Tune />, roles: ["SYSTEM_ADMIN"] },
  { to: "/ai-usage", label: "Yapay Zeka", icon: <Insights />, roles: ["SYSTEM_ADMIN"] },
  { to: "/system", label: "Sistem", icon: <Dns />, roles: ["SYSTEM_ADMIN"] },
  {
    to: "/okul-yonetimi",
    label: "Okul Yönetimi",
    subLabel: "Sınıf, Öğretmen, Öğrenci",
    icon: <Groups />,
    roles: ["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"],
    pinned: true,
  },
  {
    to: "/guidance",
    label: "Rehberlik Hizmeti",
    subLabel: "Veli raporu, görüşme vb.",
    icon: <SupportAgent />,
    roles: ["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"],
    pinned: true,
  },
  {
    to: "/muhasebe",
    label: "Muhasebe / Kayıt",
    subLabel: "Sözleşme, tahsilat, gelir/gider",
    icon: <ReceiptLong />,
    roles: ["SCHOOL_ADMIN", "ACCOUNTANT"],
    // pinned yalnız muhasebeci için (aşağıdaki isPinnedItem) — okul yöneticisinde "Daha fazla" altında.
  },
  { to: "/defined-packages", label: "Tanımlı Paketler", subLabel: "Sınıflara paket atama", icon: <LibraryBooks />, roles: ["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"] },
  { to: "/denemeler", label: "Denemeler", subLabel: "Optik deneme ve karneler", icon: <DocumentScanner />, roles: ["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"] },
  { to: "/mesaj-gonderim", label: "Mesaj Gönderim", subLabel: "Toplu SMS veya push bildirim", icon: <Sms />, roles: ["SCHOOL_ADMIN"] },
  { to: "/ders-programi", label: "Ders Programı", subLabel: "Haftalık okul programı", icon: <CalendarMonth />, roles: ["SCHOOL_ADMIN"] },
  { to: "/ai-kullanim", label: "AI Kullanımı", subLabel: "Yapay zeka harcamaları ve bakiye", icon: <Insights />, roles: ["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"] },
  { to: "/packages", label: "Paketler", subLabel: "Ders, kitap ve sorular", icon: <Inventory2 />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/video-solutions", label: "Video Çözümler", subLabel: "Soru video çözümleri", icon: <VideoLibrary />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/gunun-sorusu", label: "Günün Sorusu", subLabel: "Sınıf sınıf günlük soru", icon: <TodayOutlined />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/optik-denemeler", label: "Optik Denemeler", subLabel: "Optik form ve karneler", icon: <DocumentScanner />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/credit-management", label: "Kredi Yönetimi", subLabel: "Okullara öğrenci kredisi gönder", icon: <MonetizationOn />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/sales", label: "Satışlar", subLabel: "Uygulama içi paket satışların", icon: <PointOfSale />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/maskotlar", label: "Maskotlar", subLabel: "Mobil maskot ve açılış görseli", icon: <EmojiEmotions />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/yayinevi-mesajlar", label: "Mesajlar", subLabel: "Gönderilen e-postalar", icon: <MarkEmailRead />, roles: ["PUBLISHER_ADMIN"] },
  { to: "/yayinevi-ayarlari", label: "Ayarlar", subLabel: "E-posta (SMTP) ayarları", icon: <Settings />, roles: ["PUBLISHER_ADMIN"] },
];
// NOT: Okul kullanıcısında "AI Kullanımı" sol menüde "Daha fazla" altında (hasAiUsage koşuluyla; yukarıda NAV'da).
// Yayınevinde ise (sol menüde "Daha fazla" yok) hâlâ profil pop-up menüsünde yer alır.

/** Per-type icon for a sidebar favorite, mirroring the icon shown on its source screen. */
const favoriteIcon = (type: FavoriteTargetType): JSX.Element => {
  switch (type) {
    case "EXAM_BOOK":
      return <LibraryBooks color="primary" />;
    case "LECTURE_BOOK":
      return <AutoStories color="primary" />;
    case "PACKAGE_EXAM":
      return <Quiz sx={{ color: "secondary.main" }} />;
    case "COURSE":
      return <MenuBook color="primary" />;
    case "COURSE_LECTURES":
      return <AutoStories color="primary" />;
    case "COURSE_QUESTION_BANK":
      return <LibraryBooks sx={{ color: "secondary.main" }} />;
    case "COURSE_SCREENING_TEST":
      return <FactCheck sx={{ color: "secondary.main" }} />;
  }
};

export const AppShell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { user, clearAuth, hasAnyRole } = useAuth();
  const { isCounselor, isAccountant } = usePermission();
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = localStorage.getItem("sidebarOpen");
    return stored === null ? true : stored === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  // Okul menüsünde "Daha fazla": sabit öğeler hep görünür, gerisi bu bayrakla açılıp kapanır.
  const [moreOpen, setMoreOpen] = useState(false);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
      return;
    }
    setSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarOpen", String(next));
      return next;
    });
  };

  const closeMobileSidebar = () => {
    if (isMobile) setMobileOpen(false);
  };

  const isSystemAdmin = hasAnyRole(["SYSTEM_ADMIN"]);
  const isSchoolUser = hasAnyRole(["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR", "ACCOUNTANT"]);
  const isPublisherAdmin = hasAnyRole(["PUBLISHER_ADMIN"]);

  // Ana Sayfa'ya her gelişte "Daha fazla" kapanır; ikincil bir rotaya doğrudan gidilince açılır.
  useEffect(() => {
    if (!isSchoolUser) return;
    const path = location.pathname;
    if (path === "/") setMoreOpen(false);
    else if (SECONDARY_PATHS.some((p) => path.startsWith(p))) setMoreOpen(true);
  }, [location.pathname, isSchoolUser]);

  const { data: systemStats } = useQuery({
    queryKey: ["dashboard", "system-admin"],
    enabled: isSystemAdmin,
    queryFn: async () => {
      const { data } = await apiClient.get<DashboardStats>("/system-admin/dashboard");
      return data;
    },
  });

  const { data: schoolStats } = useQuery({
    // Muhasebeci /school-admin/dashboard'a erişemez (yalnız /accounting) → yalnız yönetici/rehber çağırır.
    queryKey: ["dashboard", "school-admin"],
    enabled: hasAnyRole(["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"]),
    queryFn: async () => {
      const { data } = await apiClient.get<SchoolDashboardStats>("/school-admin/dashboard");
      return data;
    },
  });

  // Kapalı bir okul modülünün (Muhasebe/Mesaj Gönderim/Ders Programı) rotasına doğrudan gidilirse
  // ana sayfaya dön. schoolStats yüklenene kadar bekle (aksi halde modülü açık okul yanlışlıkla yönlenir).
  useEffect(() => {
    if (!hasAnyRole(["SCHOOL_ADMIN", "GUIDANCE_COUNSELOR"])) return;
    if (schoolStats == null) return;
    const gated = SCHOOL_MODULE_PREFIXES.find((p) => location.pathname.startsWith(p));
    if (!gated) return;
    const enabled =
      gated === "/muhasebe" ? (schoolStats.accountingModuleEnabled ?? true)
      : gated === "/mesaj-gonderim" ? (schoolStats.smsModuleEnabled ?? true)
      : (schoolStats.timetableModuleEnabled ?? true);
    if (!enabled) navigate("/", { replace: true });
  }, [schoolStats, location.pathname, navigate, hasAnyRole]);

  const { data: favorites = [] } = useQuery({
    queryKey: ["favorites"],
    enabled: isPublisherAdmin,
    queryFn: () => listFavorites(),
  });

  // Yayınevi canlı öğrenci sayısı + adı için özet (Ana Sayfa ile aynı önbellek anahtarı).
  const { data: pubOverview } = useQuery({
    queryKey: ["publisher-analytics-overview"],
    enabled: isPublisherAdmin,
    queryFn: () => analyticsApi.overview(),
  });

  const badgeMap: Record<string, number | undefined> = isSystemAdmin && systemStats
    ? {
        "/schools": systemStats.totalSchools,
        "/publishers": systemStats.totalPublishers,
        ...(systemStats.pendingSchoolRequests > 0 ? { "/system": systemStats.pendingSchoolRequests } : {}),
      }
    : isSchoolUser && schoolStats
    ? {
        "/defined-packages": schoolStats.validPackageCount,
        "/denemeler": schoolStats.paperExamCount,
      }
    : isPublisherAdmin && pubOverview
    ? {
        "/packages": pubOverview.totalPackages,
        ...(pubOverview.videoSolutionsCount > 0 ? { "/video-solutions": pubOverview.videoSolutionsCount } : {}),
      }
    : {};

  // Rol filtresi + okul için sayısı 0 olan (geçerli paket / deneme) linkleri gizle.
  const visibleNav = NAV.filter((n) => hasAnyRole(n.roles)).filter((n) => {
    if (n.to === "/defined-packages") return (schoolStats?.validPackageCount ?? 0) > 0;
    if (n.to === "/denemeler") return (schoolStats?.paperExamCount ?? 0) > 0;
    if (n.to === "/ai-kullanim") return schoolStats?.hasAiUsage ?? false;
    // Okula-özel modüller (varsayılan açık): ilgili bayrak kapalıysa menüde gizli.
    if (n.to === "/muhasebe") return schoolStats?.accountingModuleEnabled ?? true;
    if (n.to === "/mesaj-gonderim") return schoolStats?.smsModuleEnabled ?? true;
    if (n.to === "/ders-programi") return schoolStats?.timetableModuleEnabled ?? true;
    // Maskot yönetimi yalnız özelliği açık yayınevinde görünür (backend flag). Kapalıysa link gizli.
    if (n.to === "/maskotlar") return pubOverview?.mascotUploadEnabled ?? false;
    // Optik Denemeler yalnız Optik Form özelliği açık yayınevinde görünür (varsayılan kapalı).
    if (n.to === "/optik-denemeler") return pubOverview?.opticalFormEnabled ?? false;
    // Satışlar yalnız en az bir satış kaydı olan yayınevinde görünür.
    if (n.to === "/sales") return pubOverview?.hasSales ?? false;
    return true;
  });

  // Okul kullanıcısında sabit (hep görünür) ve "Daha fazla" altındaki ikincil öğeler.
  // Muhasebe muhasebeci için sabit (tek modülü); okul yöneticisinde "Daha fazla" altına iner.
  // Yayınevi kaynaklı içerikler (paketler + denemeler) menünün en altında ayrı "Yayınevi" kategorisinde.
  const isPublisherItem = (n: NavItem) => n.to === "/defined-packages" || n.to === "/denemeler";
  const isPinnedItem = (n: NavItem) =>
    n.to === "/muhasebe" ? isAccountant : !!n.pinned;
  const pinnedNav = visibleNav.filter((n) => !isPublisherItem(n) && isPinnedItem(n));
  const secondaryNav = visibleNav.filter((n) => !isPublisherItem(n) && !isPinnedItem(n));
  // Sayısı 0 olan paket/deneme linkleri visibleNav'da zaten elenir; ikisi de yoksa liste boş → "Yayınevi" başlığı hiç basılmaz.
  const publisherNav = visibleNav.filter(isPublisherItem);

  // "AI Kullanımı" profil menüsünde yalnız en az bir AI kullanım kaydı varsa görünür (okul/yayınevi).
  const showAiUsage = isPublisherAdmin ? (pubOverview?.hasAiUsage ?? false) : (schoolStats?.hasAiUsage ?? false);

  const isSelected = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
  const activeItem = visibleNav.find((n) => isSelected(n.to));
  // "Ana Sayfa" artık menüde yok; başlık üst barda yine de gösterilsin diye "/" özel-durumu.
  const pageTitle =
    location.pathname === "/profile"
      ? "Profil"
      : location.pathname === "/"
      ? "Ana Sayfa"
      : activeItem?.label ?? "";

  const onLogout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch {
      // ignore — clearing local state is what matters
    }
    clearAuth();
    navigate("/login", { replace: true });
  };

  const initials = user ? `${user.name[0] ?? ""}${user.lastName[0] ?? ""}` : "";
  const roleLabel = isPublisherAdmin
    ? "Yayınevi"
    : isSystemAdmin
    ? "Sistem Yöneticisi"
    : isCounselor
    ? "Rehber Öğretmen"
    : hasAnyRole(["SCHOOL_ADMIN"])
    ? "Okul Yöneticisi"
    : isAccountant
    ? "Muhasebe"
    : "";
  const onlineCount = pubOverview?.live.online ?? 0;
  const onlinePillBg =
    onlineCount > 0
      ? theme.palette.mode === "light"
        ? "rgba(14,156,134,.12)"
        : "rgba(53,214,190,.14)"
      : theme.palette.action.hover;

  // Tek bir NavItem listesini render eder (grup başlığı + badge dahil). "Daha fazla" için sabit/ikincil
  // alt-listelerde ayrı ayrı çağrılır; her çağrıda `prev` kendi listesi içinde hesaplanır.
  const renderNav = (items: NavItem[]) =>
    items.map((item, i) => {
      const prev = items[i - 1];
      const showGroup = item.group && item.group !== prev?.group;
      return (
        <Box key={item.to}>
          {showGroup && (
            <Typography
              variant="overline"
              sx={{ display: "block", px: 2.5, pt: 1.5, pb: 0.5, color: "text.disabled" }}
            >
              {item.group}
            </Typography>
          )}
          <ListItemButton
            component={Link}
            to={item.to}
            selected={isSelected(item.to)}
            onClick={closeMobileSidebar}
            sx={{
              mx: 1,
              my: 0.25,
              py: 0.85,
              textDecoration: "none",
              color: "inherit",
              position: "relative",
              "&.Mui-selected": {
                fontWeight: 650,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 7,
                  bottom: 7,
                  width: 3,
                  borderRadius: "0 3px 3px 0",
                  bgcolor: "primary.main",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, "& svg": { fontSize: 18 } }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.subLabel}
              primaryTypographyProps={{ fontSize: 13.5 }}
              secondaryTypographyProps={{ fontSize: 11, noWrap: true, color: "text.disabled" }}
            />
            {badgeMap[item.to] != null && (
              <Chip
                label={badgeMap[item.to]}
                size="small"
                sx={{ height: 20, minWidth: 28, fontSize: 11, fontWeight: 600 }}
              />
            )}
          </ListItemButton>
        </Box>
      );
    });

  // Yayınevi kaynaklı linkler (Tanımlı Paketler + Denemeler) — hem tam menüde hem kısıtlı çekirdek menüde
  // aynı "Yayınevi" kategorisi başlığı altında gösterilir.
  const publisherSection = publisherNav.length > 0 && (
    <>
      <Divider sx={{ my: 1, mx: 2 }} />
      <Typography
        variant="overline"
        sx={{ display: "block", px: 2.5, color: "text.disabled", lineHeight: 2 }}
      >
        Yayınevi
      </Typography>
      <List dense disablePadding>{renderNav(publisherNav)}</List>
    </>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Marka başlığı */}
      <Box
        component={Link}
        to="/"
        onClick={closeMobileSidebar}
        sx={{
          height: BAR_HEIGHT,
          flexShrink: 0,
          px: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <Box
          component="img"
          src="/brand/zekabin-mark.png"
          alt="Zekabin"
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2.5,
            flexShrink: 0,
            boxShadow: theme.shadows[1],
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
            Zekabin
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.disabled", lineHeight: 1.2 }} noWrap>
            {isPublisherAdmin ? "Yayınevi Paneli" : "Yönetim Paneli"}
          </Typography>
        </Box>
      </Box>

      {/* Çoklu-okul hızlı değiştirici — tek okulluda bileşen null döner, sidebar değişmez. */}
      <SchoolSwitcher />

      {/* Menü + favoriler */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1.5 }}>
        {isSchoolUser ? (
          <>
            <List dense disablePadding>{renderNav(pinnedNav)}</List>
            {(secondaryNav.length > 0 || publisherNav.length > 0) && (
              <>
                <Divider sx={{ my: 1, mx: 2 }} />
                {moreOpen ? (
                  <>
                    {secondaryNav.length > 0 && (
                      <List dense disablePadding>{renderNav(secondaryNav)}</List>
                    )}
                    {publisherSection}
                  </>
                ) : (
                  <List dense disablePadding>
                  <ListItemButton
                    onClick={() => setMoreOpen(true)}
                    sx={{ mx: 1, my: 0.25, py: 0.85, color: "text.secondary" }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, "& svg": { fontSize: 18 } }}>
                      <ExpandMore />
                    </ListItemIcon>
                    <ListItemText
                      primary="Daha fazla"
                      primaryTypographyProps={{ fontSize: 13.5 }}
                    />
                  </ListItemButton>
                  </List>
                )}
              </>
            )}
          </>
        ) : (
          <List dense disablePadding>{renderNav(visibleNav)}</List>
        )}

        {isPublisherAdmin && favorites.length > 0 && (
          <>
            <Divider sx={{ my: 1, mx: 2 }} />
            <Typography variant="overline" sx={{ display: "block", px: 2.5, color: "text.disabled", lineHeight: 2 }}>
              Favoriler
            </Typography>
            <List dense disablePadding>
              {favorites.map((f) => {
                const to = favoriteLink(f);
                const key = `${f.targetType}-${f.targetId}-${f.packageId}`;
                return (
                  <ListItemButton
                    key={key}
                    component={Link}
                    to={to}
                    // Detay sayfasından "geri" eski kart ekranına değil paket ağacına dönsün.
                    state={{ backTo: `/packages/${f.packageId}` }}
                    selected={location.pathname + location.search === to}
                    onClick={closeMobileSidebar}
                    sx={{ mx: 1, my: 0.25, py: 0.5, textDecoration: "none", color: "inherit" }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, "& svg": { fontSize: 18 } }}>
                      {favoriteIcon(f.targetType)}
                    </ListItemIcon>
                    <ListItemText
                      primary={f.title}
                      secondary={f.subtitle}
                      primaryTypographyProps={{ fontSize: 13.5, noWrap: true }}
                      secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          ...(isMobile
            ? {}
            : {
                width: sidebarOpen ? DRAWER_WIDTH : 0,
                flexShrink: 0,
                whiteSpace: "nowrap",
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }),
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            overflowX: "hidden",
            borderColor: "divider",
            ...(isMobile
              ? {}
              : {
                  width: sidebarOpen ? DRAWER_WIDTH : 0,
                  transition: theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Üst bar (kolon içi, sticky) */}
        <Box
          component="header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: theme.zIndex.appBar,
            height: BAR_HEIGHT,
            px: { xs: 2, sm: 3 },
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: theme.palette.mode === "light" ? "rgba(255,255,255,0.82)" : "rgba(21,21,31,0.82)",
            backdropFilter: "saturate(1.4) blur(10px)",
          }}
        >
          <IconButton
            edge="start"
            onClick={toggleSidebar}
            size="small"
            aria-label={sidebarOpen ? "Menüyü gizle" : "Menüyü göster"}
          >
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.02em", minWidth: 0 }} noWrap>
            {pageTitle}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {isCounselor && (
            <Chip size="small" variant="outlined" label="Salt okunur" sx={{ display: { xs: "none", sm: "flex" } }} />
          )}
          {isPublisherAdmin && pubOverview && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                display: { xs: "none", sm: "flex" },
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                bgcolor: onlinePillBg,
                color: onlineCount > 0 ? "success.main" : "text.secondary",
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: onlineCount > 0 ? "success.main" : "text.disabled",
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>
                {onlineCount.toLocaleString("tr-TR")} çevrimiçi
              </Typography>
            </Stack>
          )}
          <ThemeControls />
          {/* Avatar → hesap menüsü (ikona bağlı pop-up: profil, AI kullanımı, çıkış). */}
          <Avatar
            onClick={(e: MouseEvent<HTMLElement>) => setProfileAnchor(e.currentTarget)}
            sx={{ width: 32, height: 32, fontSize: 14, bgcolor: "primary.main", cursor: "pointer" }}
          >
            {initials.toUpperCase()}
          </Avatar>
        </Box>

        <Box sx={{ flex: 1, p: 3 }}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </Box>

        <Box
          component="footer"
          sx={{
            py: 1.25,
            px: 3,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {new Date().getFullYear()} Zekabin. Tüm hakları saklıdır.
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography variant="caption" color="text.disabled">
            v1.0
          </Typography>
        </Box>
      </Box>

      {/* Hesap menüsü — profil ikonuna bağlı pop-up (profil, AI kullanımı, kullanıcı yönetimi, çıkış). */}
      <Popover
        open={!!profileAnchor}
        anchorEl={profileAnchor}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 340, maxWidth: "calc(100vw - 24px)", borderRadius: 2.5, overflow: "hidden" } } }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontWeight: 700,
              fontSize: 17,
              background: `linear-gradient(150deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
            }}
          >
            {initials.toUpperCase()}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              {user ? `${user.name} ${user.lastName}` : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
              {roleLabel}
            </Typography>
            {(user?.phone || user?.email) && (
              <Typography variant="caption" color="text.disabled" sx={{ display: "block" }} noWrap>
                {[user?.phone, user?.email].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Box>
        </Stack>

        <List disablePadding sx={{ py: 1 }}>
          <ListItemButton
            onClick={() => {
              setProfileAnchor(null);
              navigate("/profile");
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ManageAccounts />
            </ListItemIcon>
            <ListItemText primary="Profil" secondary="Hesap bilgileriniz" />
          </ListItemButton>
          {isPublisherAdmin && showAiUsage && (
            <ListItemButton
              onClick={() => {
                setProfileAnchor(null);
                navigate("/ai-kullanim");
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Insights />
              </ListItemIcon>
              <ListItemText primary="AI Kullanımı" secondary="Yapay zeka harcamaları ve bakiye" />
            </ListItemButton>
          )}
          {hasAnyRole(["SCHOOL_ADMIN"]) && (
            <ListItemButton
              onClick={() => {
                setProfileAnchor(null);
                navigate("/users");
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <GroupAdd />
              </ListItemIcon>
              <ListItemText primary="Kullanıcı Yönetimi" secondary="Okul yöneticileri ve rehber öğretmenler" />
            </ListItemButton>
          )}
        </List>

        <Box sx={{ p: 2, pt: 1 }}>
          <Button
            fullWidth
            variant="contained"
            color="error"
            startIcon={<Logout />}
            onClick={() => {
              setProfileAnchor(null);
              setLogoutConfirm(true);
            }}
          >
            Çıkış Yap
          </Button>
        </Box>
      </Popover>

      <ConfirmDialog
        open={logoutConfirm}
        destructive
        title="Çıkış yap"
        message="Oturumunuz kapatılacak. Çıkış yapmak istediğinize emin misiniz?"
        confirmLabel="Çıkış Yap"
        onConfirm={() => {
          setLogoutConfirm(false);
          onLogout();
        }}
        onClose={() => setLogoutConfirm(false)}
      />
    </Box>
  );
};
